const TermsOfService = () => {
  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Legal</p>
        <h1 className="text-4xl md:text-5xl text-[#EDEDED] mb-4" data-testid="tos-title">Terms of Service</h1>
        <p className="text-[#A1A1AA] mb-12">Last updated: August 9, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-[#EDEDED] leading-relaxed">
            These Terms govern your access to Breedling&trade; (&ldquo;Breedling,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). 
            By using <a href="https://breedling.com" className="text-[#D4AF37] hover:underline">https://breedling.com</a>, you agree to these Terms.
          </p>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">1. The Platform</h2>
            <p className="text-[#A1A1AA] mb-4">
              Breedling provides a marketplace and subscription-based software service connecting buyers with verified breeders, 
              service providers, transporters, and shelters/rescues. The Platform includes listing services, a live auction engine, 
              a transporter registry, a vendor/service provider directory, a reputation system, and related communication tools.
            </p>
            <p className="text-[#D4AF37] font-medium">
              Breedling is not a party to transactions between users and does not own, sell, transport, guarantee, or inspect animals.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">2. Eligibility</h2>
            <p className="text-[#A1A1AA]">
              You must be at least 18 years old to use Breedling. By registering an account, you represent and warrant that 
              all information you provide is accurate, current, and complete.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">3. Accounts &amp; Authentication</h2>
            <p className="text-[#A1A1AA] mb-4">
              Users authenticate via Google OAuth. You are responsible for maintaining the security of your account.
              Sellers, transporters, and service providers may be subject to additional identity and business verification.
            </p>
            <p className="text-[#A1A1AA]">
              Breedling reserves the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, 
              or pose a risk to the Platform community.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">4. Subscriptions &amp; Fees</h2>
            <p className="text-[#A1A1AA] mb-4">
              Breedling&trade; offers tiered subscriptions (Free, Basic, Starter, Professional, and Royalty) with varying listing limits, 
              platform fee rates, features, and visibility tiers. Activating a breeder plan requires a one-time $5.99 account activation fee. 
              Paid subscription fees are billed in advance on a recurring monthly basis through Stripe and are non-refundable except as expressly stated below.
            </p>

            <h3 className="text-lg text-[#EDEDED] mb-2">4.1 Cancellation Policy</h3>
            <p className="text-[#A1A1AA] mb-2">
              You may cancel your paid subscription at any time from the <strong className="text-[#EDEDED]">Billing</strong> tab in your 
              Breeder Dashboard. Upon cancellation:
            </p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li>Your cancellation takes effect at the <strong className="text-[#EDEDED]">end of the current billing period</strong>. 
              You will retain full access to all features of your current plan until that date.</li>
              <li>After the billing period ends, your account will automatically revert to the <strong className="text-[#EDEDED]">Free</strong> tier 
              (2 listing limit, 10% platform fee, no verified badge).</li>
              <li>Listings exceeding the Free tier limit will be deactivated (not deleted). 
              You may reactivate them by re-subscribing to a qualifying plan.</li>
              <li>Active auctions are not affected by cancellation. Any auction in progress will continue until its scheduled end.</li>
            </ul>

            <h3 className="text-lg text-[#EDEDED] mb-2">4.2 Reactivation</h3>
            <p className="text-[#A1A1AA] mb-4">
              If you change your mind after cancelling, you may <strong className="text-[#EDEDED]">reactivate</strong> your subscription 
              at any time before the current billing period ends. Reactivation restores your plan as if cancellation was never requested, 
              with no additional charges or interruption of service.
            </p>

            <h3 className="text-lg text-[#EDEDED] mb-2">4.3 Refund Policy</h3>
            <p className="text-[#A1A1AA] mb-4">
              Subscription fees are <strong className="text-[#EDEDED]">non-refundable</strong>. Because cancellation allows you to retain 
              full access through the remainder of your paid period, no partial or prorated refunds will be issued. 
              If you believe a charge was made in error, contact{" "}
              <a href="mailto:support@breedling.com" className="text-[#D4AF37] hover:underline">support@breedling.com</a>{" "}
              within 14 days of the charge for review. Refunds for erroneous charges are at Breedling's sole discretion.
            </p>

            <h3 className="text-lg text-[#EDEDED] mb-2">4.4 Founding Member Terms</h3>
            <p className="text-[#A1A1AA] mb-4">
              Founding Members who received promotional fee waivers are subject to the same cancellation policy. 
              If a Founding Member cancels during their waiver period and later re-subscribes, the remaining waiver credit 
              is forfeited and standard pricing will apply.
            </p>

            <h3 className="text-lg text-[#EDEDED] mb-2">4.5 Price Changes</h3>
            <p className="text-[#A1A1AA] mb-4">
              Breedling reserves the right to change subscription pricing with at least 30 days' written notice. 
              Continued use of the Platform after a price change constitutes acceptance of the new pricing. 
              If you do not agree with a price change, you may cancel your subscription before the change takes effect.
            </p>

            <h3 className="text-lg text-[#EDEDED] mb-2">4.6 Annual Registration Fee</h3>
            <p className="text-[#A1A1AA]">
              Every breeder plan, including the Free tier, is subject to a <strong className="text-[#EDEDED]">$59.99/year annual registration fee</strong>,
              billed on your Breedlingversary (the anniversary of your signup date). This fee maintains your breeder account, 
              verified status when applicable, and continued platform access. Founding Members who received a registration fee waiver are exempt 
              for the duration specified in their founding tier. If you cancel your subscription, the annual registration fee 
              is not refunded but no further annual charges will apply.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">5. Marketplace Transactions</h2>
            <p className="text-[#A1A1AA] mb-4">
              Payments between buyers and sellers are processed via Stripe Connect. 
              Breedling may collect platform fees from transactions.
            </p>
            <p className="text-[#A1A1AA] mb-2">Breedling is not responsible for:</p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li>The quality, legality, or health of animals or services listed</li>
              <li>Delivery, transportation, or shipping arrangements</li>
              <li>Health certifications or veterinary records</li>
              <li>Disputes between buyers, sellers, transporters, or service providers</li>
            </ul>
            <p className="text-[#D4AF37] font-medium">
              Users are responsible for complying with all applicable local, state, and federal laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">6. Live Auctions and Protected Payments</h2>
            <p className="text-[#A1A1AA] mb-4">
              Verified bidders may participate in auctions offered by eligible sellers. Auction access is separate from breeder subscription plans.
              Bids are binding commitments, reserve prices may apply, and bids placed near closing may extend the auction to reduce last-second sniping.
            </p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 mb-6">
              <li><strong className="text-[#EDEDED]">Bidder verification:</strong> A valid payment method is required before bidding. The winning bidder is charged automatically when the auction closes successfully.</li>
              <li><strong className="text-[#EDEDED]">Protected payment:</strong> Breedling keeps the collected payment in its Stripe platform balance until release conditions are met. This is a contractual payment hold and is not a bank escrow account.</li>
              <li><strong className="text-[#EDEDED]">Delivery review:</strong> After the seller reports delivery, the buyer may confirm receipt or report a problem. If no dispute is opened, seller payout is released 72 hours after reported delivery.</li>
              <li><strong className="text-[#EDEDED]">Disputes:</strong> A timely dispute pauses seller payout. Breedling may review available evidence and either refund the buyer or release the seller payout.</li>
            </ul>

            <div className="overflow-x-auto mb-6">
              <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-[#D4AF37]/40 text-[#D4AF37]">
                    <th className="p-3">Seller plan</th>
                    <th className="p-3">Winning buyer pays</th>
                    <th className="p-3">Breedling platform commission</th>
                    <th className="p-3">Seller receives</th>
                  </tr>
                </thead>
                <tbody className="text-[#A1A1AA]">
                  <tr className="border-b border-white/10"><td className="p-3">Free</td><td className="p-3">Winning bid + 3% payment processing fee</td><td className="p-3">10% of winning bid</td><td className="p-3">90% of winning bid</td></tr>
                  <tr className="border-b border-white/10"><td className="p-3">Basic, Starter, Professional</td><td className="p-3">Winning bid + 3% payment processing fee</td><td className="p-3">3% of winning bid</td><td className="p-3">97% of winning bid</td></tr>
                  <tr className="border-b border-white/10"><td className="p-3">Royalty</td><td className="p-3">Winning bid + 3% payment processing fee</td><td className="p-3">1% of winning bid</td><td className="p-3">99% of winning bid</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-[#D4AF37] font-medium">
              All auction payments must remain on Breedling. Attempts to move a transaction off-platform may lead to account restrictions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">7. Reputation System</h2>
            <p className="text-[#A1A1AA] mb-4">
              Breedling maintains a Buyer Reputation System to promote trust and accountability. Your reputation score 
              is calculated based on:
            </p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li>Completed purchases and on-time payments (+10 points)</li>
              <li>Late payments (+5 points, reduced credit)</li>
              <li>Failure to pay after winning an auction (-25 points)</li>
            </ul>
            <p className="text-[#A1A1AA] mb-4">
              Reputation tiers: <span className="text-[#22C55E]">Trusted</span> (75+), 
              <span className="text-[#F59E0B]"> Standard</span> (40-74), 
              <span className="text-[#EF4444]"> Risky</span> (below 40).
            </p>
            <p className="text-[#A1A1AA]">
              Users with a "Risky" reputation tier may be restricted from bidding on high-value auctions ($5,000 and above). 
              Breedling reserves the right to modify reputation scores or tiers at its discretion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">8. Transporter Registry</h2>
            <p className="text-[#A1A1AA] mb-4">
              Breedling provides a directory of animal transporters. Transporters must complete an onboarding process 
              and are subject to admin verification. Listing as a transporter does not constitute endorsement by Breedling.
            </p>
            <p className="text-[#A1A1AA]">
              Breedling is not responsible for the actions, availability, or service quality of transporters listed on the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">9. Vendor / Service Provider Registry</h2>
            <p className="text-[#A1A1AA] mb-4">
              Breedling provides a directory of animal-related service providers (groomers, trainers, farriers, boarding, 
              veterinary, and transport). Service providers must register and are subject to admin verification before 
              appearing in public search results.
            </p>
            <p className="text-[#A1A1AA]">
              Breedling does not endorse, guarantee, or assume liability for any vendor's services, qualifications, or conduct.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">10. Notifications &amp; Communications</h2>
            <p className="text-[#A1A1AA] mb-4">
              By using Breedling, you consent to receive transactional notifications related to your account activity, 
              including but not limited to:
            </p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1">
              <li>Outbid alerts when another user places a higher bid on a watched auction</li>
              <li>Auction ending-soon reminders for watched auctions</li>
              <li>Auction won/lost notifications and payment instructions</li>
              <li>Shelter and vendor verification status updates</li>
              <li>Administrative communications regarding your account</li>
            </ul>
            <p className="text-[#A1A1AA] mt-4">
              Notifications are delivered via email and in-app alerts. You may manage notification preferences in your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">11. Content Moderation</h2>
            <p className="text-[#A1A1AA] mb-4">
              Users may report listings that violate community guidelines. Breedling operates a moderation queue 
              and reserves the right to remove, flag, or restrict any listing or account at its sole discretion.
            </p>
            <p className="text-[#A1A1AA]">
              Listings depicting illegal species, prohibited animals, or fraudulent content will be removed and may 
              result in permanent account suspension.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">12. Prohibited Use</h2>
            <p className="text-[#A1A1AA] mb-2">You may not:</p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1">
              <li>Use the Platform for illegal activity or to list prohibited species</li>
              <li>Misrepresent listings, health documents, or credentials</li>
              <li>Circumvent platform fees or conduct off-platform transactions for auction items</li>
              <li>Manipulate auction outcomes through shill bidding or bid rigging</li>
              <li>Harass, threaten, or defraud other users</li>
              <li>Upload harmful, misleading, or inappropriate content</li>
              <li>Create multiple accounts to evade reputation penalties or restrictions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">13. Termination</h2>
            <p className="text-[#A1A1AA]">
              We may suspend or terminate accounts for violations, risk concerns, or at our sole discretion. 
              Upon termination, any active auctions you are participating in will be void, and pending transactions 
              may be canceled.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">14. Intellectual Property</h2>
            <p className="text-[#A1A1AA]">
              The Platform and its content belong to Breedling or its licensors. 
              Users grant Breedling a non-exclusive, worldwide license to display, reproduce, and distribute 
              listing content for the purpose of operating the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">15. Disclaimers</h2>
            <p className="text-[#A1A1AA]">
              The Platform is provided "as is" and "as available." Breedling makes no warranties, express or implied, 
              regarding the accuracy, reliability, or completeness of listings, auction outcomes, reputation scores, 
              transporter services, vendor services, or transactions between users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">16. Limitation of Liability</h2>
            <p className="text-[#A1A1AA]">
              To the fullest extent permitted by law, Breedling is not liable for indirect, incidental, special, 
              consequential, or punitive damages, including but not limited to loss of profits, data, goodwill, 
              or other intangible losses arising from your use of the Platform, auction participation, or transactions 
              with other users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">17. Indemnification</h2>
            <p className="text-[#A1A1AA]">
              You agree to indemnify and hold harmless Breedling, its officers, directors, employees, agents, and affiliates 
              from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) 
              arising out of or relating to: (a) your use of the Platform; (b) your violation of these Terms; (c) your violation 
              of any rights of another party; (d) any transaction, auction, or dispute with another user; or (e) your conduct 
              as a seller, bidder, transporter, or service provider on the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">18. Dispute Resolution</h2>
            <p className="text-[#A1A1AA] mb-4">
              Any disputes arising from these Terms or use of the Platform shall first be resolved through good-faith negotiation. 
              If unresolved within 30 days, disputes shall be settled by binding arbitration administered by the American Arbitration 
              Association under its Commercial Arbitration Rules.
            </p>
            <p className="text-[#A1A1AA]">
              You waive any right to participate in a class action lawsuit or class-wide arbitration against Breedling.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">19. Governing Law</h2>
            <p className="text-[#A1A1AA]">
              These Terms are governed by the laws of the State of Georgia, United States, 
              without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">20. Severability</h2>
            <p className="text-[#A1A1AA]">
              If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">21. Contact</h2>
            <p className="text-[#A1A1AA]">
              Email: <a href="mailto:support@breedling.com" className="text-[#D4AF37] hover:underline">support@breedling.com</a>
            </p>
            <p className="text-[#A1A1AA]">
              Website: <a href="https://breedling.com/support" className="text-[#D4AF37] hover:underline">https://breedling.com/support</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
